// src/components/equipements/ListeProblemesEquipement.tsx — SCE Issues
// La liste « Problèmes déclarés » de la fiche équipement (10/10). Le reste de
// la fiche ne change pas.
//
//  - Filtres légers, cumulables : statut, métier (« Mon métier » pour un
//    coordinateur), badge qualité. Seules les valeurs présentes sur la
//    machine sont proposées, avec leur nombre ; rien au-delà de 2 problèmes.
//  - Ce qui reste à traiter d'abord, puis ce qui est terminé.
//  - Sur PC, dans la fenêtre : un clic ouvre l'aperçu du problème dans le
//    volet de droite (ApercuProbleme). Ailleurs : la fiche, comme avant.

import { useMemo, useState, type CSSProperties } from 'react'
import { useDansModale, useVoletModale } from '@/components/layout/ModaleRoute'
import { ECRAN_VOLET } from '@/components/issues/FenetreQualite'
import { EtatProbleme, LIBELLE_STATUT_LISTE } from '@/components/ui/EtatProbleme'
import { ApercuProbleme } from '@/components/equipements/ApercuProbleme'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { getMetiersCoordonnes } from '@/config/coordinateurs.config'
import {
  FILTRES_VIDES,
  filtrerProblemes,
  nbFiltres,
  optionsFiltres,
  quandProbleme,
  trierProblemes,
  type FiltresProblemes,
} from '@/utils/problemesEquipement'
import type { Issue } from '@/types/issue.types'

/** En deçà, pas de filtres : la liste se lit d'un coup d'œil. */
const MIN_POUR_FILTRER = 3

export function ListeProblemesEquipement({
  issues,
  onOuvrirFiche,
}: {
  issues: Issue[]
  /** Ouvre la fiche complète (la liste visible sert de « suite »). */
  onOuvrirFiche: (issue: Issue, suite: Issue[]) => void
}): JSX.Element {
  const dansModale = useDansModale()
  const large = useMediaQuery(ECRAN_VOLET)
  const { volet } = useVoletModale()
  const voletDispo = dansModale && large && !!volet

  const { user, isCoordinateur } = useCurrentUser()
  const mesMetiers = useMemo(
    () => (isCoordinateur && user ? getMetiersCoordonnes(user.upn) : []),
    [isCoordinateur, user],
  )

  const [filtres, setFiltres] = useState<FiltresProblemes>(FILTRES_VIDES)
  const [choisi, setChoisi] = useState<string | null>(null)

  const options = useMemo(() => optionsFiltres(issues), [issues])
  const { aTraiter, termines } = useMemo(() => trierProblemes(filtrerProblemes(issues, filtres)), [issues, filtres])
  const ordre = useMemo(() => [...aTraiter, ...termines], [aTraiter, termines])

  const basculer = <K extends keyof FiltresProblemes>(cle: K, valeur: FiltresProblemes[K][number]): void => {
    setFiltres((f) => {
      const liste = f[cle] as FiltresProblemes[K][number][]
      const suivante = liste.includes(valeur) ? liste.filter((v) => v !== valeur) : [...liste, valeur]
      return { ...f, [cle]: suivante }
    })
  }

  // « Mon métier » : les métiers coordonnés, présents sur la machine.
  const mesMetiersPresents = mesMetiers.filter((m) => options.metiers.some((o) => o.valeur === m))
  const monMetierActif = mesMetiersPresents.length > 0 && mesMetiersPresents.every((m) => filtres.metiers.includes(m))
  const basculerMonMetier = (): void =>
    setFiltres((f) => ({
      ...f,
      metiers: monMetierActif ? f.metiers.filter((m) => !mesMetiersPresents.includes(m)) : [...new Set([...f.metiers, ...mesMetiersPresents])],
    }))

  const ouvrir = (issue: Issue): void => {
    if (voletDispo) setChoisi(issue.id)
    else onOuvrirFiche(issue, ordre)
  }

  const selection = choisi ? issues.find((i) => i.id === choisi) : undefined
  const rang = selection ? ordre.findIndex((i) => i.id === selection.id) : -1
  const avecFiltres = issues.length >= MIN_POUR_FILTRER

  return (
    <>
      {avecFiltres && (
        <div className="flex flex-wrap items-center" style={{ gap: 6, marginBottom: 10 }} role="group" aria-label="Filtrer les problèmes">
          {options.statuts.map((o) => (
            <Puce key={o.valeur} actif={filtres.statuts.includes(o.valeur)} onClick={() => basculer('statuts', o.valeur)}>
              {LIBELLE_STATUT_LISTE[o.valeur]} <Nb>{o.nb}</Nb>
            </Puce>
          ))}
          {(mesMetiersPresents.length > 0 || options.metiers.length > 1) && <Separateur />}
          {mesMetiersPresents.length > 0 && (
            <Puce actif={monMetierActif} onClick={basculerMonMetier}>
              Mon métier <Nb>{issues.filter((i) => mesMetiersPresents.includes(i.metier)).length}</Nb>
            </Puce>
          )}
          {options.metiers.length > 1 &&
            options.metiers
              .filter((o) => !(mesMetiersPresents.length === 1 && mesMetiersPresents[0] === o.valeur))
              .map((o) => (
                <Puce key={o.valeur} actif={filtres.metiers.includes(o.valeur)} onClick={() => basculer('metiers', o.valeur)}>
                  {o.valeur} <Nb>{o.nb}</Nb>
                </Puce>
              ))}
          {options.badges.length > 0 && <Separateur />}
          {options.badges.map((o) => (
            <Puce key={o.valeur} actif={filtres.badges.includes(o.valeur)} onClick={() => basculer('badges', o.valeur)}>
              {o.valeur} <Nb>{o.nb}</Nb>
            </Puce>
          ))}
          {nbFiltres(filtres) > 0 && (
            <button
              type="button"
              onClick={() => setFiltres(FILTRES_VIDES)}
              style={{ border: 'none', background: 'none', padding: '0 4px', fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}
            >
              Tout afficher
            </button>
          )}
        </div>
      )}

      {avecFiltres && ordre.length === 0 && (
        <div className="text-center py-5 rounded" style={{ backgroundColor: 'var(--color-white)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-copy2)' }}>
          Aucun problème ne correspond à ces filtres.
        </div>
      )}

      {[
        { titre: 'À traiter', liste: aTraiter },
        { titre: 'Terminés', liste: termines },
      ]
        .filter((g) => g.liste.length > 0)
        .map((g, n, groupes) => (
          <div key={g.titre} className="flex flex-col" style={{ gap: 8, marginTop: n > 0 ? 14 : 0 }}>
            {/* Sous-titre seulement s'il y a deux groupes : sinon il n'apprend rien. */}
            {groupes.length > 1 && (
              <span style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                {g.titre} · {g.liste.length}
              </span>
            )}
            {g.liste.map((issue) => (
              <LigneProbleme key={issue.id} issue={issue} choisi={issue.id === choisi} onClick={() => ouvrir(issue)} />
            ))}
          </div>
        ))}

      {voletDispo && selection && (
        <ApercuProbleme
          issue={selection}
          onFermer={() => setChoisi(null)}
          onOuvrirFiche={() => onOuvrirFiche(selection, ordre)}
          {...(rang > 0 ? { onPrecedent: () => setChoisi(ordre[rang - 1]!.id) } : {})}
          {...(rang >= 0 && rang < ordre.length - 1 ? { onSuivant: () => setChoisi(ordre[rang + 1]!.id) } : {})}
        />
      )}
    </>
  )
}

function LigneProbleme({ issue, choisi, onClick }: { issue: Issue; choisi: boolean; onClick: () => void }): JSX.Element {
  const termine = issue.statut === 'Clos' || issue.statut === 'Annulé'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={choisi}
      className="flex items-center justify-between gap-3 w-full text-left"
      style={{
        backgroundColor: choisi ? '#FFFBD1' : 'var(--color-white)',
        borderRadius: 10,
        padding: '10px 14px',
        border: 'none',
        boxShadow: choisi ? 'inset 0 0 0 2px var(--color-black)' : 'none',
        cursor: 'pointer',
      }}
    >
      <div className="min-w-0">
        <div style={{ fontSize: 'var(--font-size-copy2)', fontWeight: termine ? 500 : 600, color: termine ? 'var(--color-gray-90)' : 'var(--color-text-primary)' }}>
          {issue.id} — {issue.titre}
        </div>
        <div className="flex items-center flex-wrap" style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', marginTop: 2, gap: 6 }}>
          <span>
            {issue.metier} · {issue.criticite}
            {quandProbleme(issue) && ` · ${quandProbleme(issue)}`}
          </span>
          {(issue.badgesCloture ?? []).map((b) => (
            <span key={b} style={{ fontSize: 11, padding: '0 7px', borderRadius: 999, border: '1px solid var(--color-gray-30)', color: 'var(--color-gray-90)', lineHeight: '18px' }}>
              {b}
            </span>
          ))}
        </div>
      </div>
      {/* Le badge de statut, comme partout. */}
      <span style={{ flexShrink: 0 }}>
        <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
      </span>
    </button>
  )
}

function Puce({ actif, onClick, children }: { actif: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  const style: CSSProperties = {
    height: 30,
    padding: '0 11px',
    borderRadius: 15,
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer',
    border: `1px solid ${actif ? 'var(--color-black)' : 'var(--color-gray-30)'}`,
    background: actif ? 'var(--color-black)' : 'var(--color-white)',
    color: actif ? 'var(--color-white)' : 'var(--color-text-primary)',
    fontWeight: actif ? 600 : 400,
  }
  return (
    <button type="button" aria-pressed={actif} onClick={onClick} style={style}>
      {children}
    </button>
  )
}

function Nb({ children }: { children: React.ReactNode }): JSX.Element {
  return <span style={{ opacity: 0.6, fontWeight: 400 }}>{children}</span>
}

function Separateur(): JSX.Element {
  return <span aria-hidden="true" style={{ width: 1, height: 20, background: 'var(--color-gray-20)', margin: '0 4px' }} />
}
